import React from 'react';
import { Box, Divider, useTheme } from '@mui/material';
import { OrderPageTypography } from '../themedComponents/OrderPageTypography';
import { customerFacingPriceFormatter, salesTaxText, shouldNotSpecifyTax } from '../utils';
import { AccountData } from '../../api';

type BreakdownRow = {
  label: string;
  amount: number;
  muted?: boolean;
  negative?: boolean;
};

type OrderPagePriceBreakdownProps = {
  rows: BreakdownRow[];
  totalLabel: string;
  total: number;
  notes: string[];
  account: AccountData;
  withTax?: boolean;
};

export const OrderPagePriceBreakdown = ({
  rows,
  totalLabel,
  total,
  notes,
  account,
  withTax = false,
}: OrderPagePriceBreakdownProps) => {
  const theme = useTheme();
  const taxRate = account.locations?.[0]?.stripe_tax_rate;

  return (
    <Box display="flex" flexDirection="column" gap={1} width="100%">
      {rows.map((row) => (
        <Box
          key={row.label}
          display="flex"
          justifyContent="space-between"
          alignItems="baseline"
          gap={2}
          sx={{ opacity: row.muted ? 0.7 : 1 }}>
          <OrderPageTypography>{row.label}</OrderPageTypography>
          <Box component="span" dir="ltr" sx={{ unicodeBidi: 'isolate' }}>
            <OrderPageTypography>
              {row.negative ? '-' : ''}
              {customerFacingPriceFormatter(row.amount, account)}
            </OrderPageTypography>
          </Box>
        </Box>
      ))}
      <Divider style={{ backgroundColor: theme.customTheme.palette.border }} />
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="baseline" gap={2}>
          <OrderPageTypography bold variant="h3">
            {totalLabel}
          </OrderPageTypography>
          <Box component="span" dir="ltr" sx={{ unicodeBidi: 'isolate', whiteSpace: 'nowrap' }}>
            <OrderPageTypography bold variant="h3">
              {customerFacingPriceFormatter(total, account)}
            </OrderPageTypography>
          </Box>
        </Box>
        {withTax && !!taxRate && (
          <OrderPageTypography variant="h6" style={{ opacity: 0.7, textAlign: 'end' }}>
            {salesTaxText(taxRate, shouldNotSpecifyTax(account))}
          </OrderPageTypography>
        )}
      </Box>
      {notes.map((note) => (
        <OrderPageTypography key={note} variant="h6" style={{ opacity: 0.7 }}>
          {note}
        </OrderPageTypography>
      ))}
    </Box>
  );
};
